import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d6dfs7eqd308q1a0a1fg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://public.readdy.ai/ai/img_res/76b41292-0ab5-48cb-87cb-7b9ade541248.png" alt="KAM D.O.O. Logo" className="h-10 w-10 object-contain" />
            <h1 className="text-xl font-bold text-gray-900">KAM D.O.O.</h1>
          </div>
          <div className="flex items-center gap-10">
            <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Features</button>
            <button onClick={() => scrollToSection('security')} className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Security</button>
            <button onClick={() => scrollToSection('experience')} className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Experience</button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center gap-16">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-6">iOS DEVELOPMENT EXPERTS</p>
              <h2 className="text-7xl font-extralight text-gray-900 leading-tight mb-8">Secure Your<br />Digital Life</h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                We develop modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>
            <div className="flex-1">
              <div className="relative">
                <img 
                  src="https://public.readdy.ai/ai/img_res/3d545dd76fa9f5a2e9fd1a263fb12ce8.jpg" 
                  alt="iOS App Interface" 
                  className="w-full h-auto rounded-3xl shadow-2xl object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-6xl font-extralight text-gray-900 mb-6">Our Products</h2>
            <p className="text-gray-600">Professional tools for iOS security and optimization</p>
          </div>
          <div className="grid grid-cols-3 gap-8" data-product-shop>
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-12 text-white shadow-lg">
              <div className="flex items-center justify-center mb-8">
                <div className="w-24 h-24 flex items-center justify-center">
                  <i className="ri-shield-check-line text-7xl"></i>
                </div>
              </div>
              <h3 className="text-2xl font-medium mb-4 text-center">VPN Protection</h3>
              <p className="text-white/80 text-center leading-relaxed">
                Military-grade encryption to protect your online privacy and secure your internet connection on any network.
              </p>
            </div>
            <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-3xl p-12 text-white shadow-lg">
              <div className="flex items-center justify-center mb-8">
                <div className="w-24 h-24 flex items-center justify-center">
                  <i className="ri-brush-line text-7xl"></i>
                </div>
              </div>
              <h3 className="text-2xl font-medium mb-4 text-center">Device Cleaner</h3>
              <p className="text-white/80 text-center leading-relaxed">
                Optimize your device performance by removing junk files, clearing cache, and managing storage efficiently.
              </p>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-pink-500 rounded-3xl p-12 text-white shadow-lg">
              <div className="flex items-center justify-center mb-8">
                <div className="w-24 h-24 flex items-center justify-center">
                  <i className="ri-shield-star-line text-7xl"></i>
                </div>
              </div>
              <h3 className="text-2xl font-medium mb-4 text-center">Complete Security</h3>
              <p className="text-white/80 text-center leading-relaxed">
                Comprehensive protection combining VPN security with device optimization for ultimate peace of mind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <h2 className="text-6xl font-extralight text-gray-900 mb-16">Why Choose Our Solutions</h2>
          <div className="overflow-x-auto pb-8 -mx-8 px-8">
            <div className="flex gap-6 min-w-max">
              <div className="w-96 h-[480px] bg-white rounded-2xl p-8 shadow-sm">
                <div className="h-64 flex items-center justify-center mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=abstract%20minimalist%20illustration%20of%20encryption%20lock%20security%20shield%20geometric%20shapes%20blue%20gradient%20clean%20simple%20background%20modern%20flat%20design%20professional&width=300&height=250&seq=feature-001&orientation=landscape" 
                    alt="Military-Grade Encryption" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Military-Grade Encryption</h3>
                <p className="text-gray-600 leading-relaxed">
                  Bank-level AES-256 encryption ensures your data remains private and secure at all times.
                </p>
              </div>
              <div className="w-96 h-[480px] bg-white rounded-2xl p-8 shadow-sm">
                <div className="h-64 flex items-center justify-center mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=abstract%20minimalist%20illustration%20of%20one%20tap%20touch%20gesture%20finger%20click%20simple%20geometric%20teal%20gradient%20clean%20background%20modern%20flat%20design%20professional&width=300&height=250&seq=feature-002&orientation=landscape" 
                    alt="One-Tap Connection" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">One-Tap Connection</h3>
                <p className="text-gray-600 leading-relaxed">
                  Connect to our secure VPN servers instantly with a single tap. No complicated setup required.
                </p>
              </div>
              <div className="w-96 h-[480px] bg-white rounded-2xl p-8 shadow-sm">
                <div className="h-64 flex items-center justify-center mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=abstract%20minimalist%20illustration%20of%20cleaning%20optimization%20sparkle%20broom%20geometric%20shapes%20cyan%20gradient%20clean%20simple%20background%20modern%20flat%20design%20professional&width=300&height=250&seq=feature-003&orientation=landscape" 
                    alt="Smart Cleaning" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Smart Cleaning</h3>
                <p className="text-gray-600 leading-relaxed">
                  Intelligent algorithms identify and remove unnecessary files to free up valuable storage space.
                </p>
              </div>
              <div className="w-96 h-[480px] bg-white rounded-2xl p-8 shadow-sm">
                <div className="h-64 flex items-center justify-center mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=abstract%20minimalist%20illustration%20of%20battery%20power%20energy%20optimization%20lightning%20bolt%20geometric%20green%20gradient%20clean%20simple%20background%20modern%20flat%20design%20professional&width=300&height=250&seq=feature-004&orientation=landscape" 
                    alt="Battery Optimization" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Battery Optimization</h3>
                <p className="text-gray-600 leading-relaxed">
                  Extend your device battery life with smart power management and background app control.
                </p>
              </div>
              <div className="w-96 h-[480px] bg-white rounded-2xl p-8 shadow-sm">
                <div className="h-64 flex items-center justify-center mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=abstract%20minimalist%20illustration%20of%20privacy%20protection%20eye%20shield%20lock%20geometric%20shapes%20purple%20gradient%20clean%20simple%20background%20modern%20flat%20design%20professional&width=300&height=250&seq=feature-005&orientation=landscape" 
                    alt="Privacy Protection" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Privacy Protection</h3>
                <p className="text-gray-600 leading-relaxed">
                  Keep your personal information safe from trackers, ads, and malicious websites.
                </p>
              </div>
              <div className="w-96 h-[480px] bg-white rounded-2xl p-8 shadow-sm">
                <div className="h-64 flex items-center justify-center mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=abstract%20minimalist%20illustration%20of%2024%207%20support%20headset%20customer%20service%20geometric%20shapes%20orange%20gradient%20clean%20simple%20background%20modern%20flat%20design%20professional&width=300&height=250&seq=feature-006&orientation=landscape" 
                    alt="24/7 Support" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">24/7 Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our dedicated support team is always available to help you with any questions or issues.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-32 bg-gray-900">
        <div className="max-w-7xl mx-auto px-8 text-center">
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-16 flex items-center justify-center">
              <i className="ri-award-line text-5xl text-amber-500"></i>
            </div>
          </div>
          <h2 className="text-5xl font-extralight text-white mb-16">Enterprise-Level Security</h2>
          <div className="grid grid-cols-2 gap-20 max-w-5xl mx-auto">
            <div className="text-left">
              <p className="text-gray-400 leading-relaxed">
                Our applications utilize industry-standard encryption protocols and security measures to ensure your data remains protected. We implement the same security standards used by financial institutions and government agencies worldwide.
              </p>
            </div>
            <div className="text-left">
              <p className="text-gray-400 leading-relaxed">
                We are committed to your privacy. We maintain a strict no-logs policy and never collect, store, or share your personal information. Your digital security and privacy are our top priorities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-right mb-20">
            <h2 className="text-6xl font-extralight text-gray-900 mb-4">User Experience First</h2>
            <p className="text-gray-600">Designed with simplicity and efficiency in mind</p>
          </div>
          
          <div className="space-y-32">
            <div className="flex items-center gap-16">
              <div className="flex-1 relative">
                <span className="text-9xl font-extralight text-gray-200 absolute -top-8 left-0">01</span>
                <div className="relative z-10 pt-16">
                  <h3 className="text-3xl font-light text-gray-900 mb-6">Intuitive Interface</h3>
                  <p className="text-gray-600 leading-relaxed max-w-md">
                    Our applications feature clean, modern interfaces that are easy to navigate. Every element is thoughtfully designed to provide a seamless user experience without unnecessary complexity.
                  </p>
                </div>
              </div>
              <div className="flex-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iOS%20app%20interface%20screenshot%20clean%20minimalist%20design%20intuitive%20navigation%20light%20background%20professional%20mobile%20UI%20user%20experience%20simple%20elegant&width=600&height=400&seq=exp-001&orientation=landscape" 
                  alt="Intuitive Interface" 
                  className="w-full h-80 object-cover rounded-2xl shadow-lg"
                />
              </div>
            </div>

            <div className="flex items-center gap-16 flex-row-reverse">
              <div className="flex-1 relative text-right">
                <span className="text-9xl font-extralight text-gray-200 absolute -top-8 right-0">02</span>
                <div className="relative z-10 pt-16">
                  <h3 className="text-3xl font-light text-gray-900 mb-6">Seamless Performance</h3>
                  <p className="text-gray-600 leading-relaxed max-w-md ml-auto">
                    Optimized for speed and efficiency, our apps run smoothly on all iOS devices. Experience lightning-fast connections and instant response times without draining your battery.
                  </p>
                </div>
              </div>
              <div className="flex-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20device%20performance%20speed%20optimization%20fast%20loading%20smooth%20animation%20light%20background%20professional%20mobile%20technology%20efficient%20modern%20clean&width=600&height=400&seq=exp-002&orientation=landscape" 
                  alt="Seamless Performance" 
                  className="w-full h-80 object-cover rounded-2xl shadow-lg"
                />
              </div>
            </div>

            <div className="flex items-center gap-16">
              <div className="flex-1 relative">
                <span className="text-9xl font-extralight text-gray-200 absolute -top-8 left-0">03</span>
                <div className="relative z-10 pt-16">
                  <h3 className="text-3xl font-light text-gray-900 mb-6">Fast Setup</h3>
                  <p className="text-gray-600 leading-relaxed max-w-md">
                    Get started in seconds with our streamlined onboarding process. No complicated configurations or technical knowledge required – just install and start protecting your device immediately.
                  </p>
                </div>
              </div>
              <div className="flex-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=quick%20easy%20setup%20installation%20process%20iOS%20app%20onboarding%20simple%20steps%20light%20background%20professional%20mobile%20user%20friendly%20modern%20clean&width=600&height=400&seq=exp-003&orientation=landscape" 
                  alt="Fast Setup" 
                  className="w-full h-80 object-cover rounded-2xl shadow-lg"
                />
              </div>
            </div>

            <div className="flex items-center gap-16 flex-row-reverse">
              <div className="flex-1 relative text-right">
                <span className="text-9xl font-extralight text-gray-200 absolute -top-8 right-0">04</span>
                <div className="relative z-10 pt-16">
                  <h3 className="text-3xl font-light text-gray-900 mb-6">Reliable Protection</h3>
                  <p className="text-gray-600 leading-relaxed max-w-md ml-auto">
                    Count on consistent, dependable security that works silently in the background. Our apps are rigorously tested to ensure they deliver reliable protection day after day.
                  </p>
                </div>
              </div>
              <div className="flex-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=reliable%20security%20protection%20shield%20trust%20dependable%20iOS%20safety%20light%20background%20professional%20mobile%20secure%20modern%20clean%20technology&width=600&height=400&seq=exp-004&orientation=landscape" 
                  alt="Reliable Protection" 
                  className="w-full h-80 object-cover rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <footer id="contact" className="bg-gray-800 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-8 py-20">
          <div className="grid grid-cols-3 gap-16 mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/76b41292-0ab5-48cb-87cb-7b9ade541248.png" alt="KAM D.O.O. Logo" className="h-8 w-8 object-contain" />
                <h3 className="text-3xl font-extralight text-white">KAM D.O.O.</h3>
              </div>
              <p className="text-gray-400 text-sm mb-12 leading-relaxed">Modern iOS applications for enhanced security</p>
              
              <div className="space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
                    <i className="ri-mail-line"></i>
                    <span>Email</span>
                  </p>
                  <a href="mailto:help@kam-doo.com" className="text-white hover:text-gray-300 transition-colors cursor-pointer">help@kam-doo.com</a>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
                    <i className="ri-phone-line"></i>
                    <span>Phone</span>
                  </p>
                  <a href="tel:+19786257701" className="text-white hover:text-gray-300 transition-colors cursor-pointer">+1 9786257701</a>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-2">
                <i className="ri-map-pin-line"></i>
                <span>Location</span>
              </p>
              <div className="text-white space-y-2">
                <p>35, Ilinden 39</p>
                <p>SKOPJE, 1000</p>
                <p>North Macedonia</p>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-4">Get in Touch</p>
              <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="contact-form">
                <input 
                  type="text" 
                  name="name" 
                  placeholder="Your Name" 
                  required
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                />
                <input 
                  type="email" 
                  name="email" 
                  placeholder="Your Email" 
                  required
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                />
                <textarea 
                  name="message" 
                  placeholder="Your Message" 
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
                ></textarea>
                <button 
                  type="submit" 
                  disabled={formStatus === 'submitting'}
                  className="w-full px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Sending...' : formStatus === 'success' ? 'Message Sent!' : formStatus === 'error' ? 'Error - Try Again' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500">© 2025 KAM D.O.O.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-400 transition-colors cursor-pointer">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
